import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useUpdateYouTubeVideo, type YouTubeVideo } from '@/api/hooks/useYouTube'

export function EditVideoDialog({
  open, onOpenChange, video, channelId,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  video: YouTubeVideo | null
  channelId: string
}) {
  const [title, setTitle] = useState(video?.title ?? '')
  const [description, setDescription] = useState(video?.description ?? '')
  const [tags, setTags] = useState((video?.tags ?? []).join(', '))
  const [privacy, setPrivacy] = useState(video?.privacyStatus ?? 'public')

  const updateMut = useUpdateYouTubeVideo()

  const handleOpenChange = (v: boolean) => {
    if (v && video) {
      setTitle(video.title)
      setDescription(video.description ?? '')
      setTags((video.tags ?? []).join(', '))
      setPrivacy(video.privacyStatus)
    }
    onOpenChange(v)
  }

  const handleSave = () => {
    if (!video) return
    if (!title.trim()) { toast.error('Informe o titulo'); return }

    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)

    updateMut.mutate(
      {
        channelId,
        videoId: video.id,
        title: title.trim(),
        description: description.trim(),
        tags: parsedTags,
        privacyStatus: privacy,
      },
      {
        onSuccess: () => {
          toast.success('Video atualizado!')
          handleOpenChange(false)
        },
        onError: (err) => {
          toast.error(`Erro: ${err.message}`)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Video</DialogTitle>
          <DialogDescription>Altere os metadados do video no YouTube</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Titulo</Label>
            <Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div>
            <Label>Descricao</Label>
            <Textarea className="mt-1.5" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div>
            <Label>Tags (separadas por virgula)</Label>
            <Input className="mt-1.5" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>

          <div>
            <Label>Privacidade</Label>
            <Select value={privacy} onValueChange={setPrivacy}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Publico</SelectItem>
                <SelectItem value="unlisted">Nao Listado</SelectItem>
                <SelectItem value="private">Privado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={updateMut.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={updateMut.isPending}>
            {updateMut.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
